import { useEffect, useState } from "react";
import { Datepicker, locale } from "@mobiscroll/react";
import { Box, Grid2 } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useRemoteFetchDepartmentMonitoringReport } from "../../../hooks/Remote/Capacity/DepartmentMonitoring/useRemoteFetchDepartmentMonitoring";
import { PermissionAssignmentWithEntityId } from "./Domain/department-monitoring.types";
import DefaultSpinner from "../../../components/LoadingSpinner/default-spinner.component";
import { MonitoringReportCard } from "./monitoring-report-card.component";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";

/**
 * @summary Monitoring Compliance with the 65% Target – Target Group: Facility Subsidiary
 * @description A monitoring report is to be created for each anchor zone to track compliance with the 65% quota.
 * 1. Leadership Quota – the number of managers in relation to the number of manager-designated workstations.
 * 2. Employee Quota – the number of employees in relation to the number of allocated workstations within the anchor zone.
 */
export function DepartmentMonitoringReport() {
  const { t, i18n } = useTranslation();
  const { dateFormat } = useTimeFormat();

  const [monitoringData, setMonitoringData] = useState<PermissionAssignmentWithEntityId[]>([]);
  const [reportDate, setReportDate] = useState<string>(DateTime.now().toFormat("yyyy-MM-dd"));

  const {
    data: remoteDepartmentMonitoringData,
    isFetching: isDepartmentMonitoringFetching,
    refetch: refetchDepartmentMonitoringData
  } = useRemoteFetchDepartmentMonitoringReport({ reportDate: reportDate });

  const changeReportDate = (picker: any) => {
    setReportDate(picker.value);
  };

  useEffect(() => {
    if (!remoteDepartmentMonitoringData?.departments) return;

    setMonitoringData(
      remoteDepartmentMonitoringData.departments.map(data => ({
        ...data,
        // BE returns 0 - 1 value
        resultingEmployeeQuota: data.resultingEmployeeQuota * 100,
        resultingSupervisorQuota: data.resultingSupervisorQuota * 100
      }))
    );
  }, [isDepartmentMonitoringFetching, reportDate]);

  return (
    <>
      {/* date select to get the matching report */}
      <Box sx={{ width: 300, ml: -2 }} data-testid="department-monitoring-report-parent">
        {/** Determine start date here */}
        <Datepicker
          label={t("Date")}
          aria-label={"datepicker"}
          data-testid={"date-single"}
          value={reportDate}
          disabled={isDepartmentMonitoringFetching}
          onChange={changeReportDate}
          returnFormat="iso8601"
          dateFormat={dateFormat.toUpperCase()}
          locale={locale[i18n.language]}
        />
      </Box>

      {/* department monitoring report data */}
      <Grid2 container direction="row" justifyContent="flex-start" alignItems="flex-start">
        {isDepartmentMonitoringFetching && (
          <DefaultSpinner style={{ padding: 0, position: "absolute", top: "50%", left: "50%" }} />
        )}

        {!isDepartmentMonitoringFetching &&
          monitoringData.length > 0 &&
          monitoringData.map(data => (
            <MonitoringReportCard
              key={data.departmentId}
              data={data}
              refetchData={refetchDepartmentMonitoringData}
            />
          ))}
      </Grid2>
    </>
  );
}
